import { cmp, isNegative } from '@fameacc/money';
import { z } from 'zod';
import { bankProviderSchema } from './bank.js';
import { ibanWellformed } from './master-data.js';
import { isoDate, nonNegativeAmount, uuid } from './posting.js';
/**
 * **Die Regelsprache der Datenregeln** — Auftrag `R-drsprache` (2026-09-01),
 * Task 3 des Plans `docs/superpowers/plans/2026-09-01-datenregeln-umsetzung.md`,
 * Spec `docs/superpowers/specs/2026-09-01-datenregeln-design.md` Abschnitt 3.
 *
 * Diese Datei ist die **erste** der drei Schärfen aus `D-9`. Die zweite und die
 * dritte stehen in Migration `0112` (`regel_version_kriterien_katalog` über die
 * Schlüsselnamen, `regel_version_kriterien_anzahl` über die Zahl); die vierte
 * gibt es nicht, weil es keine vierte Stelle gibt, an der Kriterien entstehen.
 *
 * ## Ein geschlossener Katalog und keine Ausdruckssprache
 *
 * Sechs Felder, jedes mit einem festen, kleinen Operatorsatz. **Kein siebtes
 * Feld ohne Migration und Spec-Nachtrag** — die sechs Namen stehen wörtlich
 * genauso in der CHECK-Bedingung `regel_version_kriterien_katalog`, weil genau
 * diese Zeichenketten im `jsonb` landen. Die Deckungsgleichheit ist nicht
 * behauptet, sondern gemessen: `E-585` liest `0112_datenregel_ablage.sql` und
 * hält beide Namenslisten der Migration gegen
 * {@link DATENREGEL_KRITERIENFELDER}.
 *
 * **Alle gesetzten Kriterien sind UND-verknüpft** (`D-3`). Es gibt kein `oder`,
 * keine Gruppen, keine Rückverweise — wer zwei Textvarianten desselben
 * Sachverhalts erfassen will, legt zwei Regeln an. Das ist Festlegung 2 von
 * `BANK_MUSTER` (`apps/api/src/bank/erkennung.ts`), unverändert übernommen, und
 * Konzept § 5 schliesst eine Skriptsprache ausdrücklich aus.
 *
 * ## Warum jedes Kriterium seinen Operator nennt, auch wo es nur einen gibt
 *
 * Fünf der sechs Felder haben heute genau **einen** Operator; nur `betrag` hat
 * zwei. Ein Kriterium könnte deshalb der blosse Wert sein, und `verwendungszweck:
 * 'FS-'` wäre kürzer als `verwendungszweck: { operator: 'beginnt_mit', wert:
 * 'FS-' }`.
 *
 * Es steht trotzdem so da, aus zwei Gründen. **Erstens** liest ein Mensch die
 * gespeicherte Zeile: Die Fassungshistorie (`R-drliste`) und der zur Laufzeit
 * gebaute Begründungstext einer Automatikbuchung (`D-1`) zeigen, was die Regel
 * behauptet — und „beginnt mit FS-" ist eine Aussage, „FS-" ist eine Zeichenkette.
 * **Zweitens** ist die Erweiterung um einen zweiten Operator dann kein Formwechsel
 * mehr: `betrag` hat heute schon zwei, und dass ausgerechnet dieses eine Feld
 * anders aussähe als die anderen fünf, wäre die Art von Unregelmässigkeit, an der
 * eine Oberfläche sechs Sonderfälle statt einer Schleife baut.
 *
 * ## Was hier NICHT steht
 *
 * **`kriterien_anzahl` ist kein Feld dieses Vertrags.** Die Spalte trägt die
 * Kollisionsordnung (Spec 6.1) und darf nicht lügen; sie wird über
 * {@link kriterienAnzahl} aus dem geprüften Objekt **abgeleitet** und nie vom
 * Aufrufer entgegengenommen. Ein Aufrufer, der sie mitschickte, könnte eine
 * breite Regel als spezifische ausgeben — und die CHECK-Bedingung in `0112`
 * fienge das zwar, aber erst als 500er statt als Vertragsfehler.
 *
 * **Kein Status.** Welche Stufe eine Fassung hat, entscheidet der Weg, auf dem
 * sie entsteht (`datenregel:manage` legt Entwurf und Vorschlag an,
 * `datenregel:scharfschalten` die Vollautomatik, `D-2`) — nicht ein Feld im
 * Rumpf. {@link datenregelStatusSchema} steht hier für die LESESEITE und für
 * den Vergleich mit `regel_version_status_bekannt`, nicht als Eingabefeld.
 */
/**
 * **Die sechs Kriterienfelder, in der Reihenfolge von Spec 3.1.**
 *
 * Diese Liste ist die eine Wahrheit über den Umfang des Katalogs — sowohl
 * {@link kriterienAnzahl} als auch die SQL-Übersetzung
 * (`apps/api/src/datenregeln/auswertung.ts`) laufen über sie, statt die Namen
 * ein zweites Mal aufzuzählen.
 *
 * Sie ist wörtlich deckungsgleich mit **beiden** Namenslisten aus Migration
 * `0112`: der des Katalog-CHECKs (`kriterien - ARRAY[…] = '{}'::jsonb`) und der
 * der Zählbedingung (`kriterien ? '…'`). `E-585` misst das nach.
 */
export const DATENREGEL_KRITERIENFELDER = [
    'verwendungszweck',
    'gegenparteiIban',
    'gegenparteiName',
    'betrag',
    'dienst',
    'richtung',
];
/**
 * Die vier fachlichen Fehlercodes dieses Vertrags.
 *
 * **Warum benannte Codes und nicht Meldungstexte** — dieselbe Begründung wie
 * bei `AMOUNT_SCALE_INVALID` in `posting.ts`: Über den Text ginge es auch, bis
 * zur ersten Umformulierung. `D-9` verlangt für das unbekannte Feld
 * ausdrücklich einen eigenen Fehlercode; die drei anderen stehen daneben, weil
 * sie dieselbe Behandlung an derselben Grenze brauchen.
 */
export const DATENREGEL_FEHLER = {
    /** Ein Schlüssel in `kriterien`, den der geschlossene Katalog nicht kennt (`D-9`). */
    KRITERIUM_UNBEKANNT: 'DATENREGEL_KRITERIUM_UNBEKANNT',
    /** Kein einziges Kriterium gesetzt — die Regel träfe jede Bewegung (Spec 4.2). */
    KEIN_KRITERIUM: 'DATENREGEL_KEIN_KRITERIUM',
    /** `zwischen(von, bis)` mit `von > bis` — eine Spanne, die nichts enthält. */
    BETRAGSSPANNE_VERDREHT: 'DATENREGEL_BETRAGSSPANNE_VERDREHT',
    /** Kunde UND Kreditor gesetzt — `regel_version_eine_gegenpartei` (Spec 3.2). */
    ZWEI_GEGENPARTEIEN: 'DATENREGEL_ZWEI_GEGENPARTEIEN',
};
/**
 * Der fachliche Fehlercode zu einem Zod-Befund — oder `undefined`, wenn der
 * Befund keinen trägt.
 *
 * **Warum es diese Funktion gibt und der Code nicht überall an der Stelle
 * steht.** Drei der vier Codes reiten als `params.code` auf einem
 * selbstgestellten `custom`-Befund mit, genau wie `AMOUNT_SCALE_INVALID`. Der
 * vierte kann das nicht: `unrecognized_keys` erzeugt Zod selbst aus `.strict()`,
 * und `.strict()` nimmt in Zod 3 nur eine Meldung entgegen, keinen
 * `params`-Block. Die Alternative wäre eine von Hand geschriebene
 * Schlüsselprüfung vor dem Schema — also eine **zweite** Aufzählung der sechs
 * Namen, und damit genau die Drift, gegen die dieser Vertrag gebaut ist. Die
 * Zuordnung steht deshalb an dieser einen benannten Stelle.
 */
export function datenregelFehlercode(befund) {
    if (befund.code === z.ZodIssueCode.unrecognized_keys) {
        return DATENREGEL_FEHLER.KRITERIUM_UNBEKANNT;
    }
    if (befund.code === z.ZodIssueCode.custom) {
        const code = befund.params?.code;
        if (typeof code === 'string' && bekannterCode(code))
            return code;
    }
    return undefined;
}
function bekannterCode(wert) {
    return Object.values(DATENREGEL_FEHLER).includes(wert);
}
/**
 * **Die Normalform eines Regeltextes: NFC, Leerraumfolgen zu einem
 * Leerzeichen, Ränder beschnitten.**
 *
 * Dieselben drei Schritte wie `normalisiere()` in
 * `apps/api/src/zahldienste/erkennung.ts`, und aus derselben Begründung: Zwei
 * Texte, die auf dem Bildschirm gleich aussehen, sind ohne Normalisierung zwei
 * verschiedene Zeichenketten.
 *
 * **Warum sie hier steht und nicht importiert wird.** `packages/contracts` darf
 * nicht aus `apps/api` lesen — die Abhängigkeit läuft anders herum. Sie ist
 * deshalb hier die Quelle und wird von der Auswertung
 * (`apps/api/src/datenregeln/auswertung.ts`) **importiert**, statt dort ein
 * drittes Mal geschrieben zu werden. Für die SQL-Übersetzung geht das nicht;
 * dort steht der nachgebaute Ausdruck samt seiner benannten Grenze.
 *
 * **Sie läuft schon beim Schreiben.** Der gespeicherte Kriterienwert ist damit
 * bereits die Normalform, und beide Auswertungen normalisieren nur noch die
 * BEWEGUNG. Eine Regel, deren Wert erst zur Auswertungszeit normalisiert würde,
 * hinge daran, dass beide Auswertungen es gleich tun.
 */
export function normalisiereRegeltext(text) {
    return text.normalize('NFC').replace(/\s+/gu, ' ').trim();
}
/**
 * Die kanonische Form einer IBAN: Grossbuchstaben, kein Leerraum.
 *
 * Exportiert, weil die Ableitung einer Regel aus einer Bewegung
 * (`datenregel-ableitung.ts`) und die Auswertung (`auswertung.ts`) dieselbe
 * Kanonisierung brauchen wie dieser Vertrag und sie nicht ein weiteres Mal
 * aufschreiben sollen.
 */
export function kanonischeIban(wert) {
    return wert.toUpperCase().replace(/\s+/gu, '');
}
/**
 * Ein Kriterientext: normalisiert, nicht leer, höchstens 200 Zeichen.
 *
 * **Die Obergrenze ist keine Willkür, sondern die Aussage, was ein Kriterium
 * ist**: ein Kennzeichen am Anfang eines Verwendungszwecks oder ein
 * Namensbestandteil — kein Dokument. `bank_transaction.purpose` selbst ist
 * unbegrenzt; ein Kriterium, das länger wäre als jeder gemessene
 * Verwendungszweck, träfe nichts und wäre ein Eingabefehler, den niemand sieht.
 */
const kriterientext = z
    .string()
    .transform((wert) => normalisiereRegeltext(wert))
    .refine((wert) => wert.length > 0, { message: 'Der Kriterienwert darf nicht leer sein' })
    .refine((wert) => wert.length <= 200, {
    message: 'Ein Kriterienwert ist höchstens 200 Zeichen lang',
});
/**
 * **Ob eine Bewegung dem Geldkonto zufliesst oder von ihm abgeht.**
 *
 * In der Regelsprache ist sie ein eigenes Kriterium und kein Vorzeichen am
 * Betrag — siehe {@link betragKriterium} weiter unten.
 *
 * **Der Name nennt die Sache und nicht die Ära**, weil sie älter ist als diese:
 * `apps/api/src/zahldienste/typen.ts` führt denselben Wertebereich seit der
 * Zahldienst-Ära als `Richtung`, und `Bankmuster.richtung`
 * (`apps/api/src/bank/erkennung.ts`) benutzt ihn. Beide lesen ihn seit
 * `R-drsprache` von hier, statt ihn ein zweites und drittes Mal aufzuzählen —
 * `pnpm api:enums:check` hat genau diese Doppelung gemeldet, sobald es den
 * Vertrag dafür gab. Er steht in DIESER Datei, weil sie die erste ist, die ihn
 * als Laufzeitschema braucht; ein späterer Umzug nach `bank.ts` wäre ein
 * Umzug und keine zweite Fassung.
 */
export const bewegungsrichtungSchema = z.enum(['eingang', 'ausgang']);
/**
 * Die Richtung einer Bewegung: das Vorzeichen ihres Betrags.
 *
 * Die einzige Stelle, an der aus einem Betrag eine Richtung wird — die SQL-Fassung
 * der Auswertung (`CASE WHEN bt.amount < 0 …`) ist ihr Spiegelbild. Sie liegt hier
 * und nicht in `apps/api`, weil auch die Ableitung einer Regel aus einer Bewegung
 * (`datenregel-ableitung.ts`, `D-30`) und damit die Oberfläche sie braucht: ein
 * Umzug aus `auswertung.ts`, keine zweite Fassung.
 *
 * `bank_transaction.amount` ist vorzeichenbehaftet (Migration `0030`, `E-170`); ein
 * Nullumsatz kommt nicht vor (`bank_transaction_amount_not_zero`), die Ableitung ist
 * damit total.
 */
export function richtungVon(betrag) {
    return isNegative(betrag) ? 'ausgang' : 'eingang';
}
/**
 * Der Status einer Fassung — deckungsgleich mit
 * `regel_version_status_bekannt` aus Migration `0112`, in derselben
 * Reihenfolge. `E-585` misst auch das gegen die Migrationsdatei.
 *
 * `text` plus CHECK und kein Aufzählungstyp in der Datenbank (`D-8`): `ALTER
 * TYPE … ADD VALUE` darf in PostgreSQL nicht in derselben Transaktion benutzt
 * werden, in der der neue Wert gebraucht wird.
 */
export const datenregelStatusSchema = z.enum([
    'entwurf',
    'vorschlag',
    'vollautomatik',
    'stillgelegt',
]);
/**
 * `verwendungszweck` — **`beginnt_mit` und ausdrücklich kein `enthaelt`**
 * (`D-4`).
 *
 * Festlegung 1 von `BANK_MUSTER` warnt konkret: Ein nicht verankertes Muster
 * trifft `Storno zu Rechnung FA-…` mit und bucht eine Rückerstattung als
 * Zahlung.
 *
 * **Der Vergleich unterscheidet Gross- und Kleinschreibung**, anders als bei
 * `gegenparteiName`. Das ist die Lesart, die Spec 3.1 vorgibt (die
 * Unempfindlichkeit steht dort nur beim Namen) und sie hat einen eigenen Grund:
 * Dieses Feld trägt die Nummernkreis-Kennzeichen `FS-` und `FA-`, und `E-233`
 * hält sie zeichengenau gross. Ein unempfindlicher Anfangsvergleich weitete
 * ausgerechnet das Feld, in dem die Schreibweise etwas bedeutet.
 */
const verwendungszweckKriterium = z
    .object({ operator: z.literal('beginnt_mit'), wert: kriterientext })
    .strict();
/**
 * `gegenparteiIban` — `gleich`, kanonisiert wie `kanonischeIban()` und mit
 * nachgerechneter Prüfsumme (ISO 7064 MOD 97-10, derselbe Nachbau von
 * `iban_wellformed_v1`, den `master-data.ts` führt).
 *
 * **Die Prüfsumme wird geprüft, obwohl das Feld nur verglichen wird.** Eine
 * IBAN mit Zahlendreher ist keine IBAN; sie träfe nie eine Bewegung, und die
 * Regel wäre stumm wirkungslos. Die Live-Vorschau zeigte „0 Treffer", ohne den
 * Grund zu nennen — hier steht er.
 */
const gegenparteiIbanKriterium = z
    .object({
    operator: z.literal('gleich'),
    wert: z
        .string()
        .transform((wert) => kanonischeIban(wert))
        .refine((wert) => ibanWellformed(wert), {
        message: 'IBAN hat eine unzulässige Form oder eine falsche Prüfsumme',
    }),
})
    .strict();
/**
 * `gegenparteiName` — **`enthaelt` und nichts anderes** (`D-4`), normalisiert
 * und ohne Gross-/Kleinunterscheidung.
 *
 * Ein Kontoinhabername steht nicht verlässlich am Anfang eines Feldes; dort ist
 * `enthaelt` die einzige brauchbare Form. Das Feld trägt keine Nummernkreise,
 * an denen sich ein Fehltreffer teuer auswirkt — der Unterschied zum
 * Verwendungszweck, an dem `beginnt_mit` hängt.
 */
const gegenparteiNameKriterium = z
    .object({ operator: z.literal('enthaelt'), wert: kriterientext })
    .strict();
/**
 * `betrag` — `gleich` oder `zwischen(von, bis)`, **immer `Money`**
 * (Hausregel 1, `D-5`).
 *
 * ## Der Betrag ist OHNE Vorzeichen, und das Vorzeichen ist die Richtung
 *
 * `bank_transaction.amount` ist vorzeichenbehaftet: Ein Ausgang steht negativ
 * da (Migration `0030`, `E-170`). Verglichen wird hier trotzdem der **Betrag
 * ohne Vorzeichen**, und deshalb ist die Spanne `nonNegativeAmount` und nicht
 * `centAmount`.
 *
 * Die Alternative — vorzeichenbehaftet vergleichen — wäre an zwei Stellen
 * schlechter. Sie machte die Richtung **zweimal** ausdrückbar, einmal über das
 * Kriterium `richtung` und einmal über das Vorzeichen der Grenzen; eine Regel
 * mit `richtung: ausgang` und `betrag: zwischen(10, 50)` träfe dann nie etwas,
 * obwohl sie genau das sagt, was der Anlegende meint. Und sie kehrte die Spanne
 * um: `von` müsste `-50` sein und `bis` `-10`, sonst wäre sie leer. Beides sind
 * Fallen, die erst als „die Regel greift nicht" auffallen.
 *
 * `betrag` sagt damit WIE VIEL, `richtung` sagt WOHIN — dieselbe Trennung, die
 * `BANK_MUSTER` zwischen Ausdruck und `richtung` führt.
 *
 * ## `von > bis` ist ein Vertragsfehler
 *
 * Eine verdrehte Spanne enthält nichts. Sie ist kein Sonderfall der Auswertung,
 * sondern eine Eingabe, die niemand so meint — und `regel_version` hat für sie
 * keine CHECK-Bedingung, weil die Grenzen im `jsonb` stehen. Der Vertrag ist
 * hier die einzige Stelle, die sie fangen kann.
 */
const betragKriterium = z
    .discriminatedUnion('operator', [
    z.object({ operator: z.literal('gleich'), wert: nonNegativeAmount }).strict(),
    z
        .object({
        operator: z.literal('zwischen'),
        von: nonNegativeAmount,
        bis: nonNegativeAmount,
    })
        .strict(),
])
    .superRefine((kriterium, ctx) => {
    if (kriterium.operator !== 'zwischen')
        return;
    if (cmp(kriterium.von, kriterium.bis) > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            params: { code: DATENREGEL_FEHLER.BETRAGSSPANNE_VERDREHT },
            message: 'Die untere Grenze der Betragsspanne liegt über der oberen',
        });
    }
});
/**
 * `dienst` — `gleich`, über {@link bankProviderSchema} und nicht über eine
 * zweite Aufzählung derselben drei Werte. `bank_account.provider` ist der
 * Aufzählungstyp `bank_provider` aus Migration `0028`; die Bewegung erbt ihn
 * über ihr Geldkonto.
 */
const dienstKriterium = z
    .object({ operator: z.literal('gleich'), wert: bankProviderSchema })
    .strict();
/** `richtung` — `gleich`, `eingang` oder `ausgang`. */
const richtungKriterium = z
    .object({ operator: z.literal('gleich'), wert: bewegungsrichtungSchema })
    .strict();
/**
 * **Der geschlossene Feldkatalog.**
 *
 * `.strict()` und nicht `.passthrough()`: Ein unbekanntes Feld ist ein
 * Anwendungsfehler mit eigenem Fehlercode
 * ({@link DATENREGEL_FEHLER.KRITERIUM_UNBEKANNT}, `D-9`) und keine ignorierte
 * Zusatzangabe. Auch jedes einzelne Kriterienobjekt ist `.strict()` — sonst
 * ginge `{ operator: 'beginnt_mit', wert: 'FS-', ausser: 'Storno' }` durch, und
 * das `ausser` verschwände lautlos.
 *
 * **Mindestens ein Kriterium.** Eine Regel ohne Kriterium trifft jede Bewegung
 * (Spec 4.2). Die Datenbank fängt das ebenfalls
 * (`regel_version_kriterien_anzahl` verlangt `>= 1`) — der Vertrag fängt es
 * früher und als Vertragsfehler statt als 500er.
 */
const datenregelKriterienObjekt = z
    .object({
    verwendungszweck: verwendungszweckKriterium.optional(),
    gegenparteiIban: gegenparteiIbanKriterium.optional(),
    gegenparteiName: gegenparteiNameKriterium.optional(),
    betrag: betragKriterium.optional(),
    dienst: dienstKriterium.optional(),
    richtung: richtungKriterium.optional(),
})
    .strict('Unbekanntes Kriterium — der Feldkatalog ist geschlossen');
/**
 * Das Teilschema, das die Maske für EIN Kriterienfeld anwendet — ohne das
 * `optional()` darum.
 *
 * **Warum eine Funktion und nicht das Objektschema selbst.** Das Regelsatzformat
 * (`datenregel-satz.ts`) baut seine Kriterien aus genau diesen Teilschemas und
 * braucht dafür die Form des Objekts. Würde `datenregelKriterienObjekt` selbst
 * exportiert, erkennte der Erzeuger der Schnittstellenbeschreibung es als
 * benanntes Schema und änderte `docs/openapi/openapi.json` (ein neuer Eintrag
 * unter `components`) — für eine Strecke, die gar nicht berührt wird.
 */
export function datenregelKriterienTeilschema(feld) {
    const teil = datenregelKriterienObjekt.shape[feld];
    return teil instanceof z.ZodOptional ? teil.unwrap() : teil;
}
export const datenregelKriterienSchema = datenregelKriterienObjekt.superRefine((kriterien, ctx) => {
    if (kriterienAnzahl(kriterien) === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            params: { code: DATENREGEL_FEHLER.KEIN_KRITERIUM },
            message: 'Eine Regel ohne Kriterium träfe jede Bewegung',
        });
    }
});
/**
 * **Die Spezifität einer Regel: wie viele der sechs Kriterien gesetzt sind.**
 *
 * Der Wert für `regel_version.kriterien_anzahl`. Er wird **abgeleitet** und nie
 * entgegengenommen (Task 3 des Plans): Die Spalte trägt die Kollisionsordnung
 * (Spec 6.1, `D-13`) und darf nicht lügen.
 *
 * Gezählt wird über {@link DATENREGEL_KRITERIENFELDER} und nicht über
 * `Object.keys()`. Der Unterschied ist nicht kosmetisch: `Object.keys()` zählte
 * einen Schlüssel mit, den das Schema gar nicht kennt — und dieselbe Funktion
 * soll auch über einem Objekt aus der Datenbank aufrufbar sein, das nicht
 * gerade durch `.strict()` gelaufen ist.
 */
export function kriterienAnzahl(kriterien) {
    return DATENREGEL_KRITERIENFELDER.filter((feld) => kriterien[feld] !== undefined).length;
}
/**
 * Die Kontonummer eines Ergebnisses: vier bis sechs Ziffern. Eine Teilregel, die
 * Maske (`datenregelErgebnisSchema`) und Regelsatzformat (`datenregel-satz.ts`)
 * gemeinsam benutzen — das Format hat keine zweite Kontonummernregel.
 */
export const datenregelKonto = z
    .string()
    .regex(/^[0-9]{4,6}$/u, 'Kontonummer besteht aus vier bis sechs Ziffern');
/** Der Steuerschlüssel eines Ergebnisses — dieselbe Teilregel für Maske und Regelsatz. */
export const datenregelSteuerschluessel = z.string().min(1, 'Steuerschlüssel darf nicht leer sein');
/**
 * **Das Ergebnis einer Regel** (Spec 3.2): EIN Gegenkonto (`D-6`), optional ein
 * Steuerschlüssel, optional **entweder** ein Kunde **oder** ein Kreditor.
 *
 * **Kein Split auf mehrere Konten.** Dieselbe Zurückhaltung wie bei
 * `Registerregel.gegenkonto`: Ein Split ist eine Verteilungsentscheidung mit
 * Begründung je Position (`KS-4`) — die trifft ein Mensch in der Maske, und
 * eine Regel, die sie träfe, müsste raten.
 *
 * **`konto` ist die Kontonummer und keine `account.id`.** Der Kontenrahmen ist
 * versionsgeführt; ein Verweis auf eine `account`-Zeile zeigt nach der ersten
 * Fortschreibung auf die geschlossene Fassung — dieselbe Überlegung, aus der
 * `kontenkatalog` (Migration `0075`) an der Nummer hängt. Ob die Nummer zum
 * BUCHUNGSDATUM gültig ist, prüft die Auswertung und nicht dieser Vertrag
 * (`E-597`): Das Buchungsdatum steht hier nicht zur Verfügung, und ein Konto
 * kann zwischen dem Anlegen der Regel und dem Buchen einer Bewegung schliessen.
 *
 * **Der Steuerschlüssel wird hier NICHT gegen Automatikkonten geprüft.** Die
 * Unterdrückung auf einem Automatikkonto (`E-391`, Migration `0075`) ist eine
 * Auskunft aus `kontenkatalog` und damit eine Abfrage — sie gehört an dieselbe
 * Stelle wie die Kontengültigkeit.
 */
export const datenregelErgebnisSchema = z
    .object({
    konto: datenregelKonto,
    steuerschluessel: datenregelSteuerschluessel.optional(),
    kundeId: uuid.optional(),
    kreditorId: uuid.optional(),
})
    .strict()
    .superRefine((ergebnis, ctx) => {
    if (ergebnis.kundeId !== undefined && ergebnis.kreditorId !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            params: { code: DATENREGEL_FEHLER.ZWEI_GEGENPARTEIEN },
            message: 'Eine Regel ordnet entweder einem Kunden oder einem Kreditor zu, nie beiden',
        });
    }
});
/**
 * **Eine neue Fassung** — was ein Aufrufer schickt, wenn er eine Regel anlegt
 * oder fortschreibt.
 *
 * Der Rumpf trägt **kein** `kriterienAnzahl` (abgeleitet, siehe oben), **kein**
 * `gueltigBis` (das setzt die Fortschreibung an der VORGÄNGERIN, und der
 * Anfüge-Trigger aus `0112` lässt genau diese eine Änderung zu), **keine**
 * Scharfschaltungsstempel (sie entstehen mit dem `INSERT`, `D-10`) und **keinen**
 * Status (der hängt am Weg, nicht am Rumpf, `D-2`).
 *
 * `name` steht hier und nicht auf einer eigenen Kopfform: Der Kopf entsteht beim
 * ERSTEN Anlegen zusammen mit der ersten Fassung, und eine Fortschreibung nennt
 * den Namen der Regel, die sie fortschreibt. Die Normalisierung zu `name_key`
 * (`D-16`) gehört an die Schreibstelle (`R-drcaps`) — die Datenbank hat den
 * Vertrag nicht, nach dem normalisiert wird, und dieser Vertrag hat den Bestand
 * nicht, gegen den die Eindeutigkeit gilt.
 */
export const datenregelFassungSchema = z
    .object({
    name: z
        .string()
        .transform((wert) => normalisiereRegeltext(wert))
        .refine((wert) => wert.length > 0, { message: 'Der Regelname darf nicht leer sein' })
        .refine((wert) => wert.length <= 120, {
        message: 'Ein Regelname ist höchstens 120 Zeichen lang',
    }),
    kriterien: datenregelKriterienSchema,
    ergebnis: datenregelErgebnisSchema,
    gueltigAb: isoDate,
})
    .strict();
/**
 * Die Betragsgrenzen eines `betrag`-Kriteriums als Paar — die Form, in der
 * beide Auswertungen und die SQL-Bindung sie brauchen.
 *
 * `gleich` ist die Spanne `[wert, wert]`. Das ist keine Vereinfachung, die
 * etwas verliert: Bei zwei Nachkommastellen ist `= x` dasselbe wie
 * `x <= b <= x`, und eine Form statt zweier heisst eine Stelle weniger, an der
 * die zwei Auswertungen auseinanderlaufen können.
 */
export function betragsgrenzen(kriterium) {
    return kriterium.operator === 'gleich'
        ? { von: kriterium.wert, bis: kriterium.wert }
        : { von: kriterium.von, bis: kriterium.bis };
}
//# sourceMappingURL=datenregeln.js.map